import React, { useState, useEffect } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    IconButton,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Menu,
    MenuItem,
    Divider,
    Alert
} from '@mui/material';
import {
    Folder as FolderIcon,
    FolderOpen as FolderOpenIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    CreateNewFolder as CreateNewFolderIcon,
    DriveFileRenameOutline as DriveFileRenameOutlineIcon,
    DeleteOutline as DeleteOutlineIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import tokenService from '../../services/tokenService';

interface Folder {
    id: number;
    name: string;
    parent?: { id: number };
    children?: Folder[];
}

interface FolderTreeProps {
    selectedFolderId?: number;
    onFolderSelect: (folderId?: number) => void;
}

const MAX_FOLDER_DEPTH = 3;
const FOLDER_ACTIONS_MENU_ID = 'folder-actions-menu';
const FOLDER_MANAGER_ROLES = ['ROLE_MEDIA_MANAGER', 'ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR'];

const findFolder = (folders: Folder[], id: number): Folder | undefined => {
    for (const folder of folders) {
        if (folder.id === id) return folder;
        const found = findFolder(folder.children || [], id);
        if (found) return found;
    }
    return undefined;
};

const FolderTreeItem: React.FC<{
    folder: Folder;
    level: number;
    selectedId?: number;
    onSelect: (id: number) => void;
    expanded: Set<number>;
    onToggle: (id: number) => void;
    onOpenActions?: (anchor: HTMLElement, folder: Folder) => void;
    menuOpenForId?: number;
}> = ({ folder, level, selectedId, onSelect, expanded, onToggle, onOpenActions, menuOpenForId }) => {
    const isExpanded = expanded.has(folder.id);
    const hasChildren = folder.children && folder.children.length > 0;
    const shouldShowChildren = hasChildren && level < MAX_FOLDER_DEPTH - 1;

    return (
        <>
            <ListItem
                disablePadding
                sx={{
                    '& .folder-actions': { opacity: 0 },
                    '&:hover .folder-actions, & .folder-actions:focus-visible, & .folder-actions[aria-expanded="true"]': { opacity: 1 },
                    ...(selectedId === folder.id && { '& .folder-actions': { opacity: 1 } }),
                    '@media (hover: none)': { '& .folder-actions': { opacity: 1 } },
                }}
                secondaryAction={onOpenActions && (
                    <IconButton
                        className="folder-actions"
                        edge="end"
                        sx={{ width: 40, height: 40 }}
                        aria-label={`Aktionen für Ordner ${folder.name}`}
                        aria-haspopup="menu"
                        aria-expanded={menuOpenForId === folder.id}
                        aria-controls={menuOpenForId === folder.id ? FOLDER_ACTIONS_MENU_ID : undefined}
                        onClick={(e) => onOpenActions(e.currentTarget, folder)}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                )}
            >
                {shouldShowChildren ? (
                    <IconButton
                        size="small"
                        aria-label={`Ordner ${folder.name} ${isExpanded ? 'zuklappen' : 'aufklappen'}`}
                        aria-expanded={isExpanded}
                        onClick={() => onToggle(folder.id)}
                        sx={{ position: 'absolute', left: 8 + level * 16, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}
                    >
                        {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                    </IconButton>
                ) : null}
                <ListItemButton
                    selected={selectedId === folder.id}
                    onClick={() => onSelect(folder.id)}
                    title={folder.name}
                    sx={{ pl: 1 + level * 2, pr: onOpenActions ? 6 : 2 }}
                >
                    <Box sx={{ width: 34, flexShrink: 0 }} />
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
                    </ListItemIcon>
                    <ListItemText primary={folder.name} primaryTypographyProps={{ noWrap: true }} sx={{ minWidth: 0 }} />
                </ListItemButton>
            </ListItem>
            {shouldShowChildren && isExpanded && (
                <Collapse in={isExpanded}>
                    <List component="div" disablePadding>
                    {folder.children!.map((child) => (
                        <FolderTreeItem
                            key={child.id}
                            folder={child}
                            level={level + 1}
                            selectedId={selectedId}
                            onSelect={onSelect}
                            expanded={expanded}
                            onToggle={onToggle}
                            onOpenActions={onOpenActions}
                            menuOpenForId={menuOpenForId}
                        />
                    ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

const FolderTree: React.FC<FolderTreeProps> = ({ selectedFolderId, onFolderSelect }) => {
    const [folders, setFolders] = useState<Folder[]>([]);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
    const [actionFolder, setActionFolder] = useState<Folder | undefined>();
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [busy, setBusy] = useState(false);
    const [selectedFolderDepth, setSelectedFolderDepth] = useState(0);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    useEffect(() => {
        loadFolders();
    }, []);

    useEffect(() => {
        if (selectedFolderId) {
            calculateFolderDepth(selectedFolderId);
        } else {
            setSelectedFolderDepth(0);
        }
    }, [selectedFolderId, folders]);

    const calculateFolderDepth = (folderId: number) => {
        const findDepth = (folders: Folder[], currentDepth: number): number => {
            for (const folder of folders) {
                if (folder.id === folderId) {
                    return currentDepth;
                }
                if (folder.children && folder.children.length > 0) {
                    const childDepth = findDepth(folder.children, currentDepth + 1);
                    if (childDepth > 0) return childDepth;
                }
            }
            return 0;
        };
        const depth = findDepth(folders, 1);
        setSelectedFolderDepth(depth);
    };

    const loadFolders = async () => {
        try {
            const { data } = await dataProvider.getList('media_folders', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            });

            // Build tree structure - convert parent IRI to id if needed
            const folderMap = new Map<number, Folder>();
            data.forEach((folder: any) => {
                let parentId = undefined;

                if (folder.parent) {
                    if (typeof folder.parent === 'string') {
                        // Check if it's a plain ID string like "1" or an IRI like "/api/media_folders/1"
                        if (folder.parent.includes('/')) {
                            // It's an IRI
                            const match = folder.parent.match(/\/(\d+)$/);
                            parentId = match ? parseInt(match[1], 10) : undefined;
                        } else {
                            // It's a plain ID string
                            parentId = parseInt(folder.parent, 10);
                        }
                    } else if (folder.parent.id) {
                        parentId = Number(folder.parent.id);
                    } else if (typeof folder.parent === 'object' && folder.parent['@id']) {
                        // Handle JSON-LD format
                        const match = folder.parent['@id'].match(/\/(\d+)$/);
                        parentId = match ? parseInt(match[1], 10) : undefined;
                    }
                }

                folderMap.set(folder.id, {
                    ...folder,
                    parent: parentId ? { id: parentId } : undefined,
                    children: []
                });
            });

            const rootFolders: Folder[] = [];
            folderMap.forEach((folder) => {
                if (folder.parent && folder.parent.id) {
                    const parent = folderMap.get(folder.parent.id);
                    if (parent) {
                        parent.children!.push(folder);
                    }
                } else {
                    rootFolders.push(folder);
                }
            });

            setFolders(rootFolders);
        } catch (error) {
            notify('Fehler beim Laden der Ordner', { type: 'error' });
        }
    };

    const handleToggle = (folderId: number) => {
        setExpanded(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(folderId)) {
                newExpanded.delete(folderId);
            } else {
                newExpanded.add(folderId);
            }
            return newExpanded;
        });
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) {
            notify('Bitte geben Sie einen Ordnernamen ein', { type: 'warning' });
            return;
        }

        // Check if max depth would be exceeded
        if (selectedFolderId && selectedFolderDepth >= MAX_FOLDER_DEPTH) {
            notify(`Maximale Ordnertiefe von ${MAX_FOLDER_DEPTH} Ebenen erreicht`, { type: 'warning' });
            return;
        }

        try {
            const { data: created } = await dataProvider.create('media_folders', {
                data: {
                    name: newFolderName,
                    parent: selectedFolderId ? `/api/media_folders/${selectedFolderId}` : null
                }
            });

            notify('Ordner erfolgreich erstellt', { type: 'success' });
            setCreateDialogOpen(false);
            setNewFolderName('');
            if (selectedFolderId) {
                setExpanded(prev => new Set(prev).add(selectedFolderId));
            }
            await loadFolders();
            onFolderSelect(Number(created.id));
            refresh();
        } catch (error) {
            notify('Fehler beim Erstellen des Ordners', { type: 'error' });
        }
    };

    const openActions = (anchor: HTMLElement, folder: Folder) => {
        setActionFolder(folder);
        setActionsAnchor(anchor);
    };

    const openRenameDialog = () => {
        setActionsAnchor(null);
        setRenameValue(actionFolder?.name ?? '');
        setRenameDialogOpen(true);
    };

    const openDeleteDialog = () => {
        setActionsAnchor(null);
        setDeleteError('');
        setDeleteDialogOpen(true);
    };

    const trimmedRename = renameValue.trim();
    const canSaveRename = trimmedRename !== '' && trimmedRename !== actionFolder?.name;

    const handleRenameFolder = async () => {
        if (!actionFolder || !canSaveRename || busy) return;

        setBusy(true);
        try {
            await dataProvider.update('media_folders', {
                id: actionFolder.id,
                data: { name: trimmedRename },
                previousData: actionFolder
            });
            notify('Ordner umbenannt', { type: 'success' });
            setRenameDialogOpen(false);
            await loadFolders();
            refresh();
        } catch (error: any) {
            notify(error?.body?.detail || error?.body?.['hydra:description'] || 'Fehler beim Umbenennen des Ordners', { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteFolder = async () => {
        if (!actionFolder) return;

        setBusy(true);
        try {
            await dataProvider.delete('media_folders', {
                id: actionFolder.id,
                previousData: { id: actionFolder.id }
            });
            notify('Ordner gelöscht', { type: 'success' });
            setDeleteDialogOpen(false);
            await loadFolders();
            if (selectedFolderId === actionFolder.id) {
                onFolderSelect(actionFolder.parent?.id);
            }
            refresh();
        } catch (error: any) {
            setDeleteError(error?.body?.detail || error?.body?.['hydra:description'] || 'Fehler beim Löschen des Ordners');
        } finally {
            setBusy(false);
        }
    };

    const actionFolderHasSubfolders = (actionFolder?.children?.length ?? 0) > 0;

    const canCreateSubfolder = !selectedFolderId || selectedFolderDepth < MAX_FOLDER_DEPTH;
    const canManageFolders = tokenService.getRoles().some((role) => FOLDER_MANAGER_ROLES.includes(role));
    const targetName = selectedFolderId
        ? findFolder(folders, selectedFolderId)?.name
        : 'Alle Dateien (oberste Ebene)';

    return (
        <Box>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ fontWeight: 'bold' }}>Ordner</Box>
                {canManageFolders && (
                    <Button
                        size="small"
                        startIcon={<CreateNewFolderIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                        disabled={!canCreateSubfolder}
                        title={canCreateSubfolder ? 'Neuer Ordner' : `Maximale Ordnertiefe von ${MAX_FOLDER_DEPTH} Ebenen erreicht`}
                    >
                        Neuer Ordner
                    </Button>
                )}
            </Box>

            <List>
                <ListItemButton
                    selected={selectedFolderId === undefined}
                    onClick={() => onFolderSelect(undefined)}
                    sx={{ pl: 1 }}
                >
                    <Box sx={{ width: 34, flexShrink: 0 }} />
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <FolderIcon />
                    </ListItemIcon>
                    <ListItemText primary="Alle Dateien" />
                </ListItemButton>

                {folders.map((folder) => (
                    <FolderTreeItem
                        key={folder.id}
                        folder={folder}
                        level={0}
                        selectedId={selectedFolderId}
                        onSelect={onFolderSelect}
                        expanded={expanded}
                        onToggle={handleToggle}
                        onOpenActions={canManageFolders ? openActions : undefined}
                        menuOpenForId={actionsAnchor ? actionFolder?.id : undefined}
                    />
                ))}
            </List>

            <Menu
                id={FOLDER_ACTIONS_MENU_ID}
                anchorEl={actionsAnchor}
                open={Boolean(actionsAnchor)}
                onClose={() => setActionsAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={openRenameDialog}>
                    <ListItemIcon><DriveFileRenameOutlineIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Umbenennen</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={openDeleteDialog} disabled={actionFolderHasSubfolders} sx={{ color: 'error.dark' }}>
                    <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: 'error.dark' }} /></ListItemIcon>
                    <ListItemText
                        primary="Löschen"
                        secondary={actionFolderHasSubfolders ? 'Enthält Unterordner' : undefined}
                    />
                </MenuItem>
            </Menu>

            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
                <DialogTitle>Neuer Ordner</DialogTitle>
                <DialogContent>
                    {targetName && (
                        <DialogContentText>Wird angelegt in: {targetName}</DialogContentText>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Ordnername"
                        fullWidth
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleCreateFolder();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleCreateFolder} variant="contained">Erstellen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Ordner umbenennen</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Ordnername"
                        fullWidth
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleRenameFolder();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleRenameFolder} variant="contained" disabled={!canSaveRename || busy}>Speichern</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Ordner „{actionFolder?.name}" löschen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Der Ordner wird endgültig gelöscht. Das geht nur, wenn er leer ist.
                    </DialogContentText>
                    {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleDeleteFolder} variant="contained" color="error" disabled={busy || Boolean(deleteError)}>
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FolderTree;
